import { create } from 'zustand';

import type { V1Automation } from '@/generated/hooks/grimoire/models/v1Automation';
import type { IRainSendAttachment } from '@/modules/rain/hooks/types';

/**
 * The thread a just-created draft belongs to, so the editor's chat does not have
 * to discover it. A store rather than router state because the draft edit page
 * clears `location.state` on mount (ENG-4145). Consumed once.
 */
export interface ICreatedDraftThread {
  automationId: string;
  threadId: string;
}

/**
 * The first message of a new draft, waiting for a thread to send it to.
 *
 * `isDispatched` guards against sending twice (ENG-4145). It lives in the store
 * rather than a ref because the chat body remounts when the thread arrives,
 * which is exactly when the send fires.
 */
export interface IPendingDraftMessage {
  automationId: string;
  content: string;
  attachments: IRainSendAttachment[];
  isDispatched: boolean;
}

/**
 * The automation record of a just-created draft, so the editor's first render
 * has real values instead of a spinner. Kept here and not in the shared
 * automation store, which the editor resets on unmount.
 */
export interface ICreatedDraftAutomation {
  automationId: string;
  /** The record `createAutomation` returned, whole. */
  automation: V1Automation;
}

interface ICreatedDraftThreadState {
  createdDraftAutomation: ICreatedDraftAutomation | null;
  /** Records the created automation just before handing over. */
  setCreatedDraftAutomation: (seed: ICreatedDraftAutomation) => void;
  /** Drops the seed once the editor's own query has landed. */
  clearCreatedDraftAutomation: () => void;

  createdDraftThread: ICreatedDraftThread | null;
  /** Records the thread the editor should bind to, just before handing over. */
  setCreatedDraftThread: (thread: ICreatedDraftThread) => void;
  /** Drops the seed once the editor has taken it. */
  clearCreatedDraftThread: () => void;

  pendingDraftMessage: IPendingDraftMessage | null;
  /** Stashes the typed message just before navigating to the new draft. */
  setPendingDraftMessage: (
    message: Omit<IPendingDraftMessage, 'isDispatched'>,
  ) => void;
  /**
   * Claims the message for sending. Marks it dispatched and returns it in one
   * update, so two callers in the same tick cannot both get it.
   */
  takePendingDraftMessage: (
    automationId: string,
  ) => IPendingDraftMessage | null;
  /** Drops the stash — the send finished, or the draft was abandoned. */
  clearPendingDraftMessage: () => void;
}

export const useCreatingDraftStore = create<ICreatedDraftThreadState>(
  (set, get) => ({
    createdDraftAutomation: null,
    setCreatedDraftAutomation: seed => set({ createdDraftAutomation: seed }),
    clearCreatedDraftAutomation: () => set({ createdDraftAutomation: null }),

    createdDraftThread: null,
    setCreatedDraftThread: thread => set({ createdDraftThread: thread }),
    clearCreatedDraftThread: () => set({ createdDraftThread: null }),

    pendingDraftMessage: null,
    setPendingDraftMessage: message =>
      set({ pendingDraftMessage: { ...message, isDispatched: false } }),
    takePendingDraftMessage: automationId => {
      const pending = get().pendingDraftMessage;
      if (
        !pending ||
        pending.isDispatched ||
        pending.automationId !== automationId
      ) {
        return null;
      }
      set({ pendingDraftMessage: { ...pending, isDispatched: true } });
      return pending;
    },
    clearPendingDraftMessage: () => set({ pendingDraftMessage: null }),
  }),
);

export const selectCreatedDraftAutomation = (state: ICreatedDraftThreadState) =>
  state.createdDraftAutomation;
export const selectSetCreatedDraftAutomation = (
  state: ICreatedDraftThreadState,
) => state.setCreatedDraftAutomation;
export const selectClearCreatedDraftAutomation = (
  state: ICreatedDraftThreadState,
) => state.clearCreatedDraftAutomation;

export const selectPendingDraftMessage = (state: ICreatedDraftThreadState) =>
  state.pendingDraftMessage;
export const selectSetPendingDraftMessage = (state: ICreatedDraftThreadState) =>
  state.setPendingDraftMessage;
export const selectTakePendingDraftMessage = (
  state: ICreatedDraftThreadState,
) => state.takePendingDraftMessage;
export const selectClearPendingDraftMessage = (
  state: ICreatedDraftThreadState,
) => state.clearPendingDraftMessage;

export const selectCreatedDraftThread = (state: ICreatedDraftThreadState) =>
  state.createdDraftThread;
export const selectSetCreatedDraftThread = (state: ICreatedDraftThreadState) =>
  state.setCreatedDraftThread;
export const selectClearCreatedDraftThread = (
  state: ICreatedDraftThreadState,
) => state.clearCreatedDraftThread;
